/**
 * Renders a single embedded saved-query result inside an article.
 *
 * Lifecycle on mount:
 *  1. Parse the saved query's `query_body` (JSON string) into a
 *     `MetricGroup[]`. Normalize order via `sortQuery` so backend cache
 *     keys match the Explorer's own cache key for the same query.
 *  2. POST to `/explorer/api/exec_query` via `executeQuery`, passing the
 *     `saved_query_id` so the backend logs the access and (more
 *     importantly) so the Redis cache layer hits for queries that have
 *     already been executed by another user / context.
 *  3. Parse the JSON `data` string from the response into rows.
 *  4. Render via the existing `ExplorerDataGrid` with the saved
 *     `table_state` and a page-level `valueLookup` (for filter chips).
 *
 * The `ExplorerDataGrid` is intentionally used as-is — multiple
 * instances on a page are safe (it's stateless above its local DataGrid
 * state). A more polished "embedded mode" variant (slimmer chrome,
 * different sticky behavior, etc.) is post-MVP.
 *
 * "Open in Explorer →" deep-link uses the existing
 * `/explorer/nba?q=<id>&ts=<encodedTableState>` format the full Explorer
 * page already supports. Research articles only embed NBA-environment
 * saved queries (the page is wrapped in `LeagueEnvironmentProvider(NBA)`),
 * so the path is hardcoded to `/explorer/nba`.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import ExplorerDataGrid from "../../Elements/ExplorerDataGrid/ExplorerDataGrid";
import { ValueLookup } from "../../Elements/ExplorerDataGrid/VideoCellHandler";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";
import {
  ExplorerQueryContext,
  ExplorerSource,
  MetricGroup,
  RecodedValue,
  ResultLayout,
  SeamsCrossed,
  sortQuery,
} from "../../../models/Explorer";
import QueryResultNotices from "../../Elements/QueryResultNotices/QueryResultNotices";
import { ResearchEmbeddedQuery } from "../../../models/Research";

export interface ResearchQueryEmbedProps {
  embed: ResearchEmbeddedQuery;
  /** Filter chip labels for the data grid. Built once at the article
   *  page level via `buildValueLookup(filters)` and passed down so every
   *  embed shares the same labels without re-fetching filters. */
  valueLookup: ValueLookup;
}

export default function ResearchQueryEmbed({
  embed,
  valueLookup,
}: ResearchQueryEmbedProps) {
  const api = useLeagueApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [queryContext, setQueryContext] = useState<
    ExplorerQueryContext | undefined
  >(undefined);
  const [recoded, setRecoded] = useState<RecodedValue[]>([]);
  const [seamsCrossed, setSeamsCrossed] = useState<SeamsCrossed>({});
  const [pinnedSource, setPinnedSource] = useState<ExplorerSource | null>(
    null,
  );
  const [layout, setLayout] = useState<ResultLayout | null>(null);

  const displayTitle = embed.title_override || embed.name;
  const openInExplorerUrl = `/explorer/nba?q=${encodeURIComponent(
    embed.saved_query_id,
  )}${
    embed.table_state
      ? `&ts=${encodeURIComponent(embed.table_state)}`
      : ""
  }`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    let parsed: MetricGroup[];
    try {
      parsed = JSON.parse(embed.query_body) as MetricGroup[];
    } catch (e) {
      setError("Failed to parse saved query body");
      setLoading(false);
      return;
    }

    const sorted = sortQuery(parsed);
    api
      .executeQuery(sorted, "research_embed", embed.saved_query_id)
      .then((resp) => {
        if (cancelled) return;
        const payload = resp.data;
        // exec_query returns `data` as a JSON string for size; some
        // legacy paths return the array directly. Mirror ExplorerStore's
        // parsing logic.
        const rows = payload.data
          ? JSON.parse(payload.data)
          : Array.isArray(payload)
            ? payload
            : null;
        if (rows === null) {
          setError(
            "Query results too large for the browser to process. Open in Explorer to narrow the query.",
          );
          setLoading(false);
          return;
        }
        setData(rows);
        setQueryContext(payload.queryContext);
        setRecoded(Array.isArray(payload.recoded) ? payload.recoded : []);
        setSeamsCrossed(payload.seams_crossed ?? {});
        setPinnedSource(payload.pinnedSource ?? null);
        setLayout(payload.layout ?? null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to execute embedded query";
        setError(msg);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, embed.query_body, embed.saved_query_id]);

  return (
    <Box
      sx={{
        mb: 3,
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Header: title + open-in-explorer */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          backgroundColor: "#fafafa",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {/* Match ArticleBody's h1 visual treatment so the embed title
              reads as a peer of the article's own section headings.
              `borderBottom` is omitted — the card chrome's row
              separator already serves that role. */}
          <Typography
            component="h3"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              m: 0,
            }}
          >
            {displayTitle}
          </Typography>
          {embed.description && (
            <Typography
              sx={{
                fontSize: "0.78rem",
                color: "text.secondary",
                mt: 0.25,
              }}
            >
              {embed.description}
            </Typography>
          )}
        </Box>
        <Link
          href={openInExplorerUrl}
          target="_blank"
          rel="noopener"
          underline="hover"
          sx={{
            color: "#CF0A2C",
            fontSize: "0.78rem",
            fontWeight: 600,
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          Open in Explorer
          <OpenInNewIcon sx={{ fontSize: "0.95rem" }} />
        </Link>
      </Stack>

      {/* Body: data grid or load/error state */}
      <Box sx={{ p: 1.5 }}>
        {error ? (
          <Alert severity="error" sx={{ fontSize: "0.85rem" }}>
            {error}
          </Alert>
        ) : loading ? (
          // Match the rough vertical footprint of a small data grid so
          // the page doesn't collapse-then-jump when results arrive.
          <Stack spacing={0.5}>
            <Skeleton variant="rectangular" height={32} />
            <Skeleton variant="rectangular" height={28} />
            <Skeleton variant="rectangular" height={28} />
            <Skeleton variant="rectangular" height={28} />
            <Skeleton variant="rectangular" height={28} />
          </Stack>
        ) : (
          <>
            <QueryResultNotices
              api={api}
              seamsCrossed={seamsCrossed}
              recoded={recoded}
              resolution={queryContext?.resolution}
              pinnedSource={pinnedSource}
            />
            <ExplorerDataGrid
              data={data}
              loading={false}
              valueLookup={valueLookup}
              initialTableState={embed.table_state ?? undefined}
              queryContext={queryContext}
              layout={layout}
              embedded
            />
          </>
        )}
      </Box>
    </Box>
  );
}
